import React, { useState, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import withStyles from 'isomorphic-style-loader/withStyles';
import { useIntl } from 'react-intl';
import Link from '../../../components/Link';
import { getActiveLanguages, getDefaultMobileLanguage, getPushNotificationTemplatesList } from '../../../lib/graphql';
import CustomPagination from '../../../components/CustomPagination/CustomPagination';
import CommonTable from '../../../components/CommonTable/CommonTable';
import messages from '../../../locale/jsonMessages';
import { typeToSlug } from './pushNotificationTemplateSlug';
import bt from '../../../components/commonStyle.css';

const PAGE_SIZE = 10;

function PushNotificationTemplates(props) {
  const { title } = props;
  const [searchKey, setSearchKey] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const debounceTimer = useRef(null);

  const intl = useIntl();

  const { data: defaultLangData } = useQuery(getDefaultMobileLanguage, {
    variables: { appType: 'web' },
    fetchPolicy: 'network-only',
    ssr: false
  });

  const defaultLocale =
    defaultLangData?.getDefaultMobileLanguage?.status === 200 &&
      defaultLangData?.getDefaultMobileLanguage?.result?.languageCode
      ? defaultLangData.getDefaultMobileLanguage.result.languageCode
      : 'en-US';

  const { data, previousData, loading, refetch } = useQuery(getPushNotificationTemplatesList, {
    variables: {
      searchKey,
      currentPage,
      defaultLocaleOnly: true,
      locale: defaultLocale
    },
    fetchPolicy: 'network-only',
    ssr: false
  });

  const { data: activeLanguagesData, loading: activeLanguagesLoading } = useQuery(getActiveLanguages, {
    variables: { appType: 'web' },
    fetchPolicy: 'network-only',
    ssr: false
  });


  const resolvedData = data ?? previousData;
  const list = resolvedData?.getPushNotificationTemplatesList?.results ?? [];
  const count = resolvedData?.getPushNotificationTemplatesList?.count ?? 0;
  const activeLanguages = activeLanguagesData?.getActiveLanguages?.results ?? [];

  const activeLocalesLabel = useMemo(() => {
    if (activeLanguagesLoading) return '…';
    if (!activeLanguages || activeLanguages.length === 0) {
      return intl.formatMessage(messages.pushNotificationTemplateNoActiveLanguages);
    }
    const codes = activeLanguages
      .map((l) => l?.languageCode)
      .filter(Boolean)
      .map((code) => {
        if (code === 'en-US') return 'en';
        const short = String(code).split('-')[0];
        return short || code;
      });
    const unique = codes.filter((c, idx) => codes.indexOf(c) === idx);
    return unique.join(', ');
  }, [activeLanguages, activeLanguagesLoading, intl]);

  const handleSearch = useCallback((e) => {
    const value = e.target.value || '';
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setSearchKey(value);
      setCurrentPage(1);
    }, 300);
  }, []);

  const paginationData = useCallback((page) => {
    setCurrentPage(page);
    refetch({ searchKey, currentPage: page, defaultLocaleOnly: true, locale: defaultLocale });
  }, [refetch, searchKey, defaultLocale]);

  const thead = useCallback(() => [
    { data: intl.formatMessage(messages.pushNotificationTemplateType) },
    { data: intl.formatMessage(messages.pushNotificationTemplateTitle) },
    { data: intl.formatMessage(messages.editLabel) }
  ], [intl]);

  const tbody = useCallback(() =>
    list.map((row) => ({
      id: `${row.type}-${row.locale || 'en'}`,
      data: [
        { data: <span>{row.type}</span> },
        { data: (row.title || '').substring(0, 50) + (row.title && row.title.length > 50 ? '…' : '') || '—' },
        {
          data: (
            <Link to={`/siteadmin/push-notification-templates/edit/${encodeURIComponent(typeToSlug(row.type))}`}>
              {intl.formatMessage(messages.editLabel)}
            </Link>
          )
        }
      ]
    })),
    [list, intl]);

  return (
    <div>
      <h1 className={'siteAdminHeading'}>
        {intl.formatMessage(messages.pushNotificationTemplateListLabel)}
      </h1>
      <p className={bt.commonMediumText}>
        {intl.formatMessage(messages.pushNotificationTemplatesListDescription)}
      </p>
      <CommonTable
        thead={thead}
        tbody={tbody}
        loading={loading}
        isSearch
        onSearch={handleSearch}
      />
      {count > PAGE_SIZE && (
        <CustomPagination
          total={count}
          currentPage={currentPage}
          defaultCurrent={1}
          defaultPageSize={PAGE_SIZE}
          change={paginationData}
          paginationLabel={intl.formatMessage(messages.pushNotificationTemplatesPaginationLabel)}
          isScroll
        />
      )}
    </div>
  );
}

PushNotificationTemplates.propTypes = {
  title: PropTypes.string.isRequired
};

export default withStyles(bt)(PushNotificationTemplates);